import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  type PaintRule,
  PolygonSymbolizer,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

interface Props {
  defaultVisible: boolean;
  visible?: boolean;
}

export const IDOL_FARMLAND_LAYER_NAME = "遊休農地";

const host = process.env.VITE_COMMON_TILE_HOST ?? "";
const url = `${host}/idol-farmland/idol-farmland.pmtiles`;

export const IDOL_FARMLAND_MIN_ZOOM = 17;
export const IDOL_FARMLAND_MAX_ZOOM = 22;
const IDOL_FARMLAND_Z_INDEX = LAYER_Z_INDEX.IDOL_FARMLAND;

export const buildIdolFarmlandPaintRules = (
  renderScale: number,
  zoomOffset: number
): PaintRule[] => [
  {
    dataLayer: "idol-farmland",
    symbolizer: new PolygonSymbolizer({
      fill: "steelblue",
      opacity: 0.5,
      stroke: "blue",
      width: renderScale,
    }),
    filter: (z, feature) => {
      // 遊休度が8.0以上10.0以下のものを表示
      const idleCls = feature?.props?.idle_cls;
      if (typeof idleCls !== "number") {
        return false;
      }
      return idleCls >= 8.0 && idleCls <= 10.0;
    },
    minzoom: IDOL_FARMLAND_MIN_ZOOM + zoomOffset,
    maxzoom: IDOL_FARMLAND_MAX_ZOOM + zoomOffset,
  },
];

/**
 * 遊休農地レイヤー
 */
export const IdolFarmlandLayer: React.FC<Props> = ({
  defaultVisible,
  visible: visibleProp,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      leafletLayer({
        url,
        paintRules: buildIdolFarmlandPaintRules(
          context.printScale,
          context.zoomOffset
        ),
        attribution: "遊休農地データ",
      }) as GridLayer,
    []
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === IDOL_FARMLAND_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === IDOL_FARMLAND_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!map) return;

    const layer = leafletLayer({
      url,
      paintRules: buildIdolFarmlandPaintRules(1, 0),
      attribution: "遊休農地データ",
    }) as GridLayer;

    layer.setZIndex(IDOL_FARMLAND_Z_INDEX);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible]);

  return <FeatureGroup />;
};
